import React, { useEffect, useRef, useState } from "react";
import NodeHeader from "./NodeHeader.tsx";
import { Handle, Position, NodeProps, useUpdateNodeInternals } from "reactflow";
import NodeBody from "./NodeBody.tsx";
import SIDEPANEL from "../constants/SidepanelContent.ts";
import { ContentItem, NodeData, NodeType } from "../models/types.ts";
import ActionTooltip from "./ActionTooltip.tsx";
import NodeAction from "./NodeAction.tsx";
import ContentMenu from "./ContentMenu.tsx";
import { useFlowStore } from "../store/FlowStateStore.ts";
import { CanvasNode, CanvasNodeContent, CanvasNodeData } from "../models/CanvasNode.ts";
import GoalToggle from "./GoalToggle.tsx";

const CustomNode: React.FC<NodeProps> = (data) => {
  const { nodes, edges, setNodes, setEdges } = useFlowStore();
  const updateNodeInternals = useUpdateNodeInternals();

  // Adding, removing, or reordering bubbles moves which bubble hosts the body-level
  // connector (only the last bubble renders it). React Flow caches each handle's DOM
  // position and only recomputes it on request — without this, an edge drawn to the
  // old bubble keeps rendering at its stale position once the handle moves, showing up
  // as a dangling/disconnected line even though the underlying connection is unchanged.
  const contentSignature = ((data.data as CanvasNodeData)?.content ?? [])
    .map((c: CanvasNodeContent) => c.id)
    .join(",");
  useEffect(() => {
    updateNodeInternals(data.id);
  }, [contentSignature]);

  const addSubNode = (content: ContentItem) => {
    const subNodeContent = new CanvasNodeContent({ type: content.type });
    const updatedNodes = nodes.map((node: CanvasNode) => {
      if (node.id === data.id) {
        return {
          ...node,
          data: {
            ...node.data,
            content: [...((node.data as CanvasNodeData).content as CanvasNodeContent[]), subNodeContent],
          },
        }
      }

      return node;
    });
    setNodes([...(updatedNodes as CanvasNode[])]);
    setAddContentBtn(false);
  }

  const [addContentBtn, setAddContentBtn] = useState(false);
  const addContentInfo = SIDEPANEL["Message Type"];


  const deleteNode = () => {
    setNodes(nodes.filter((node) => (node.id !== data.id)));
    setEdges(edges.filter((edge) => !((edge.target == data.id) || (edge.target == data.id))));
  }
  const deleteSubNode = (nodeContent, index) => {
    const updatedNodes = nodes.map((node: CanvasNode) => {
      if (node.id === data.id) {
        return {
          ...node,
          data: {
            ...node.data,
            content: [...((node.data as CanvasNodeData).content as CanvasNodeContent[]).filter((sContent) => (nodeContent.id != sContent.id))],
          },
        }
      }

      return node;
    });
    setNodes([...(updatedNodes as CanvasNode[])]);

  }
  const duplicateNode = (nodeData: CanvasNodeData) => {
    const newNode = {
        id: crypto.randomUUID(),
        type: "custom",
        position: { x: Math.random() * 400, y: Math.random() * 400 },
        data: {
            id: crypto.randomUUID(),
            isDrag: true,
            name: nodeData.name,
            icon: nodeData.icon,
            type: nodeData.type,
            canAddContent: nodeData.canAddContent,
            backLinkEnabled: nodeData.backLinkEnabled,
            // Copied explicitly: this object is built field by field, so anything omitted here is
            // silently dropped from the duplicate.
            isGoal: nodeData.isGoal,
            goalName: nodeData.goalName,
            content: nodeData.content.map(c => ({
                id: crypto.randomUUID(),
                type: c.type,
                data: JSON.parse(JSON.stringify(c.data))
            }))
        }
    };
    setNodes([...nodes, newNode] as CanvasNode[]);
  }
  const toggleGoal = () => {
    const updatedNodes = nodes.map((node: CanvasNode) => {
      if (node.id === data.id) {
        const nodeData = node.data as CanvasNodeData;
        // Clearing the name on un-mark keeps a stale label from being saved with the flag off and
        // reappearing, confusingly, if someone re-marks the step later.
        return {
          ...node,
          data: nodeData.isGoal
            ? { ...nodeData, isGoal: false, goalName: "" }
            : { ...nodeData, isGoal: true },
        }
      }

      return node;
    });
    setNodes([...(updatedNodes as CanvasNode[])]);
  }
  const moveNode = (direction: "UP" | "DOWN", index: number) => {
    const updatedNode = nodes.map((node) => {
      if (node.id == data.id) {
        console.log("index : ", index);
        const nodeContent = (node.data as CanvasNodeData).content;
        if (direction == "UP" && index > 0) {
          [nodeContent[index - 1], nodeContent[index]] = [nodeContent[index], nodeContent[index - 1]];
        } else if (direction == "DOWN" && (index < nodeContent.length - 1)) {
          [nodeContent[index + 1], nodeContent[index]] = [nodeContent[index], nodeContent[index + 1]];
        }
      }
      return node;
    })
    setNodes([...updatedNode]);
  }
  return (
    <div className="node-container" style={{ backgroundColor: (data.data.type != 'FLOW_START' ? 'white' : 'rgb(0, 128, 105)') }}>
      <div>
        <NodeHeader data={data.data} />
        {data.data.type !== "FLOW_START" && <div className="node-heading-action">
          <NodeAction deleteNode={deleteNode} duplicateNode={() => duplicateNode(data.data)} isGoal={data.data.isGoal} toggleGoal={toggleGoal} />
        </div>}
      </div>
      {
        data.data.content &&
        data.data.content.map((nodeContent, index) => {
          return (
            <div className="subnode-list" style={{ position: 'relative', background: '#eae6df', borderRadius: '8px', marginTop: '4px' }}>
              <NodeBody parentId={data.id} nodeContent={nodeContent.data} type={nodeContent.type} nodeId={data.data.id} subNodeId={nodeContent.id} />
              {data.data.type !== "FLOW_START" && <div className="action-tool">
                <ActionTooltip idx={index} canAddContent={data.data.canAddContent} addSubNode={() => addSubNode(nodeContent)} deleteSubNode={() => deleteSubNode(nodeContent, index)} moveNode={moveNode} length={data.data.content.length} />
              </div>}
            </div>
          )
        })
      }

      <>
        {data.data.backLinkEnabled &&
          <Handle
            type="target"
            position={Position.Left}
            id="left"
            style={{ background: "white", border: "1px solid green", height: '8px', width: '8px' }}
          />
        }
        {data.data.canAddContent &&
          <div className="relative">
            <button onClick={() => setAddContentBtn(!addContentBtn)} className="font-label add-content">+ Add Content</button>
            {addContentBtn && <ContentMenu contents={addContentInfo} addNode={null} onAddSubNode={addSubNode} closeDialog={() => setAddContentBtn(false)} />}
          </div>
        }
        {/* Every step except the start can be a goal — the start is where people arrive, not
            somewhere they can be counted as having achieved anything. */}
        {data.data.type !== "FLOW_START" &&
          <GoalToggle nodeId={data.id} isGoal={data.data.isGoal} goalName={data.data.goalName} />
        }
      </>
    </div>
  );
}

export default CustomNode;
